// src/components/TranslationPageButtons.tsx
import React, { useEffect, useState } from "react";
import axios, { apiUrlForPath } from "../utils/axiosConfig";

import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Spinner } from "react-bootstrap";

import {
  ArrowLeft,
  ArrowRight,
  Download,
  Eye,
  EyeOff,
  Pencil,
  Save as SaveIcon,
} from "lucide-react";



// ---------- Types ----------
interface ITranslation {
  id: number;
  translation_progress?: number;
  file_id: number;
  speech_generation_progress?: number;
  text_generation_progress?: number;
  audio_path: string | null;
  target_language: string;
  original_language?: string;
  voice_id?: string;
}

interface Voice {
  id: string;
  name: string;
}

interface ICost {
  translation: string;
  voiceover: string;
}
interface EditorState {
  isEditing: boolean;
  onToggleEdit: () => void;
  translatedText: string;
  onChangeTranslatedText: (v: string) => void;
  showOriginal: boolean;
  setShowOriginal: React.Dispatch<React.SetStateAction<boolean>>;
  hasTranslatedText: boolean;
  onSave?: () => Promise<void>;
}
interface PageNavigation {
  currentPageNumber: number;
  totalPages: number;
  onNextPage: () => void;
  onPreviousPage: () => void;
  goToPage: (page: number) => void;
}
interface Props {
  translation: ITranslation;
  cost: ICost | null;
  editorState: EditorState;
  pageNavigation: PageNavigation;
}

const TranslationPageButtons: React.FC<Props> = ({
  translation,
  cost,
  editorState,
  pageNavigation,
}) => {
  // ---------------- state, API ----------------
  const [actionLoading, setActionLoading] = useState(false);
  const [voiceName, setVoiceName] = useState<string | null>(null);

  useEffect(() => {
    // Fetch voice name if voice_id exists
    const fetchVoiceName = async () => {
      if (translation.voice_id) {
        try {
          const { data } = await axios.get<Voice>(
            `/v2/language/${translation.voice_id}`
          );
          setVoiceName(data.name);
        } catch (error) {
          console.error("Error fetching voice data:", error);
        }
      }
    };

    fetchVoiceName();
  }, [translation.voice_id]);

  // ---------- helpers / handlers ----------
  const {
    isEditing,
    onToggleEdit,
    translatedText,
    showOriginal,
    setShowOriginal,
    hasTranslatedText,
    onSave,
  } = editorState;

  const {
    currentPageNumber,
    totalPages,
    onNextPage,
    onPreviousPage,
    goToPage,
  } = pageNavigation;

  const handleSave = async () => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      await axios.post(
        `/v2/translation/${translation.id}/page/${currentPageNumber}/update`,
        { text: translatedText }
      );
      onToggleEdit();

      if (onSave) {
        await onSave();
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadText = async () => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      const { data } = await axios.get(
        `/v2/translation/${translation.id}/download`,
        { responseType: "blob" }
      );
      const blob = new Blob([data], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `translation_${translation.id}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadAudio = async () => {
    if (!translation.audio_path || actionLoading) return;
    setActionLoading(true);
    try {
      const response = await axios.get(
        `/v2/translation/${translation.id}/download-audio`,
        { responseType: "blob" }
      );
      
      const blob = new Blob([response.data], { type: "audio/wav" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audio_${translation.id}.wav`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading audio:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePageInputChange = (value: string) => {
    const pageNum = parseInt(value, 10);
    if (isNaN(pageNum)) return;
    if (pageNum < 1) goToPage(1);
    else if (pageNum > totalPages) goToPage(totalPages);
    else goToPage(pageNum);
  };

  const toggleOriginalText = () => setShowOriginal(!showOriginal);

  // ---------- render ----------
  return (
    <div className="flex flex-wrap gap-3 items-center">
      {/* Show / Hide */}
      {hasTranslatedText && (
        <Button
          variant="outline"
          onClick={toggleOriginalText}
          className="flex gap-2 items-center"
        >
          {showOriginal ? (
            <EyeOff className="w-4 h-4" />
          ) : (
            <Eye className="w-4 h-4" />
          )}
          {showOriginal ? "Hide Original" : "Show Original"}
        </Button>
      )}

      {/* Edit / Save */}
      {hasTranslatedText &&
        (isEditing ? (
          <Button
            id="saveButton"
            variant="outline"
            onClick={handleSave}
            disabled={actionLoading}
            className="flex gap-2 items-center"
          >
            <SaveIcon className="w-4 h-4" /> Save
          </Button>
        ) : (
          <Button
            variant="outline"
            onClick={onToggleEdit}
            disabled={actionLoading}
            className="flex gap-2 items-center"
          >
            <Pencil className="w-4 h-4" /> Edit
          </Button>
        ))}

      {/* Download */}
      {(translation.translation_progress === 100 ||
        translation.text_generation_progress === 100) && (
        <Button
          variant="outline"
          onClick={handleDownloadText}
          className="flex gap-2 items-center"
        >
          <Download className="w-4 h-4" /> Download Text
        </Button>
      )}
      {translation.audio_path &&
        translation.speech_generation_progress === 100 && (
          <Button
            variant="outline"
            onClick={handleDownloadAudio}
            className="flex gap-2 items-center"
          >
            <Download className="w-4 h-4" /> Download Audio
          </Button>
        )}

      {/* Audio */}
      {translation.audio_path &&
        translation.speech_generation_progress === 100 && (
          <>
            <audio
              controls
              className="h-9"
              src={apiUrlForPath(translation.audio_path)}
            />
            {voiceName && (
              <span className="text-sm text-muted-foreground">
                Voice: {voiceName}
              </span>
            )}
          </>
        )}

      {/* Pagination (right‑aligned) */}
      <div className="flex gap-2 items-center ml-auto">
        {hasTranslatedText && (
          <>
            <Button
              variant="ghost"
              size="icon"
              onClick={onPreviousPage}
              disabled={currentPageNumber <= 1}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>

            <Input
              type="number"
              min={1}
              max={totalPages}
              value={currentPageNumber}
              onChange={(e) => handlePageInputChange(e.target.value)}
              className="w-16 h-9 text-center"
            />

            <span className="text-sm text-muted-foreground">
              of {totalPages}
            </span>

            <Button
              variant="ghost"
              size="icon"
              onClick={onNextPage}
              disabled={currentPageNumber >= totalPages}
            >
              <ArrowRight className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>

      {actionLoading && <Spinner animation="border" variant="primary" />}
    </div>
  );
};

export default TranslationPageButtons;
